import { motion } from "framer-motion";
import styles from "Styles/Background/backgroundContainer.module.css";
import useSegment from "Hooks/useSegment";
import { useVisualContext } from "Contexts/VisualContext";
import classNames from "classnames";
import React from "react";

const BackgroundCircle = ({ isExiting }) => {
  const circleClass = classNames(
    styles.backgroundElement,
    styles.backgroundCircle,
  );

  return (
    <motion.svg
      className={circleClass}
      animate={{
        x: 100,
        y: 50,
        rotate: 180,
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        type: "tween",
        repeatType: "mirror",
      }}
      width="158"
      height="158"
      viewBox="0 0 158 158"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="79" cy="79" r="79" fill="#7DECB9" />
    </motion.svg>
  );
};

const BackgroundHorseshoe = () => {
  const horseshoeClass = classNames(
    styles.backgroundElement,
    styles.backgroundHorseshoe,
  );

  return (
    <motion.svg
      className={horseshoeClass}
      animate={{
        x: -200,
        y: 100,
        rotate: 180,
      }}
      transition={{
        duration: 60,
        repeat: Infinity,
        repeatType: "mirror",
        type: "tween",
      }}
      width="356"
      height="342"
      viewBox="0 0 356 342"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_112_226)">
        <path
          d="M189.574 272.971C268.122 251.738 314.556 170.852 293.299 92.2939L206.64 115.717C214.942 146.403 196.801 177.992 166.121 186.284C135.457 194.572 103.857 176.431 95.5544 145.744L8.88521 169.166C30.147 247.73 111.043 294.201 189.574 272.971Z"
          fill="#FD8A8A"
        />
      </g>
      <defs>
        <clipPath id="clip0_112_226">
          <rect
            width="288.507"
            height="208.749"
            fill="white"
            transform="translate(0 176.006) rotate(-37.5937)"
          />
        </clipPath>
      </defs>
    </motion.svg>
  );
};

const BackgroundStar = () => {
  const starClass = classNames(styles.backgroundElement, styles.backgroundStar);

  return (
    <motion.svg
      className={starClass}
      animate={{
        x: 200,
        y: -100,
        rotate: -360,
      }}
      transition={{
        duration: 30,
        repeat: Infinity,
        repeatType: "mirror",
      }}
      width="133"
      height="133"
      viewBox="0 0 133 133"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_112_208)">
        <path
          d="M91.8236 90.1076L104.098 90.8L99.1445 79.5391L110.935 75.9839L102.431 67.1127L112.313 59.7355L101.281 54.3284L108.059 44.0196L95.8309 42.7221L98.693 30.7254L86.747 33.7008L85.3423 21.4622L75.1134 28.3486L69.6186 17.3441L62.3423 27.3108L53.4134 18.8693L49.9718 30.7159L38.6882 25.8565L39.4913 38.15L27.2138 37.4556L32.1672 48.7165L20.3763 52.2717L28.8807 61.1429L19.0018 68.522L30.0338 73.9292L23.2563 84.238L35.4808 85.5335L32.622 97.5322L44.568 94.5568L45.9727 106.795L56.1983 99.907L61.6964 110.914L68.9694 100.945L77.8983 109.386L81.3431 97.5417L92.6268 102.401L91.8236 90.1076Z"
          fill="#FCCA23"
        />
      </g>
      <defs>
        <clipPath id="clip0_112_208">
          <rect
            width="96.3363"
            height="96.3363"
            fill="white"
            transform="translate(49.8867) rotate(31.1877)"
          />
        </clipPath>
      </defs>
    </motion.svg>
  );
};

const BackgroundTriangle = () => {
  const triangleClass = classNames(
    styles.backgroundElement,
    styles.backgroundTriangle,
  );

  return (
    <motion.svg
      className={triangleClass}
      width="337"
      height="477"
      viewBox="0 0 337 477"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M336.598 336.763L196.378 0.774414L0.449219 476.927L336.598 336.763Z"
        fill="white"
      />
    </motion.svg>
  );
};

const BackgroundFoldedShape = () => {
  const foldedShapeClass = classNames(
    styles.backgroundElement,
    styles.backgroundFoldedShape,
  );

  return (
    <motion.svg
      className={foldedShapeClass}
      width="631"
      height="534"
      viewBox="0 0 631 534"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M105.209 47.9731L0 351.522L524.958 533.471L630.167 229.922L105.209 47.9731Z"
        fill="#7DECB9"
      />
      <path
        d="M562.998 206.5L105.498 48L563.498 0L562.998 206.5Z"
        fill="#6FD7A7"
      />
    </motion.svg>
  );
};

const BackgroundCircleImage = ({ image, isExiting = false }) => {
  const bgImage = require("Assets/images/start-image.png");
  const variants = {
    in: {
      x: 0,
      y: 0,
      opacity: 1,
    },
    out: {
      x: 500,
      y: 1000,
      opacity: 0,
      scale: 0.5,
    },
  };

  return (
    <motion.div
      initial={{
        x: 300,
        y: 100,
        opacity: 0,
        scale: 0.5,
      }}
      variants={variants}
      transition={{
        duration: 0.5,
        type: "spring",
        delay: isExiting ? 0 : 0.2,
      }}
      animate={isExiting ? "out" : "in"}
      className={styles.backgroundCircleImage}
    >
      <motion.div
        className={styles.backgroundCircleImageBorder}
        animate={{
          rotate: 360,
        }}
        transition={{
          duration: 60,
          repeat: Infinity,
          repeatType: "loop",
        }}
      />
      <div
        className={styles.backgroundCircleImageInner}
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      ></div>
    </motion.div>
  );
};

const StartPageBackground = () => {
  const { transitionState } = useVisualContext();
  const segment = useSegment();
  const isStartPage = () => {
    return segment === "/";
  };

  const bgElements = {
    startPage: [
      // {
      //   element: isStartPage() ? BackgroundNoisyCircle : null,
      //   key: "noisyCircle",
      // },
      {
        element: isStartPage() ? BackgroundFoldedShape : null,
        key: "foldedShape",
      },
      {
        element: isStartPage() ? BackgroundCircleImage : null,
        key: "circleImage",
      },
      {
        element: isStartPage() ? BackgroundTriangle : null,
        key: "triangle",
      },
      {
        element: isStartPage() ? BackgroundCircle : null,
        key: "circle",
      },
      {
        element: isStartPage() ? BackgroundHorseshoe : null,
        key: "horseshoe",
      },
      {
        element: isStartPage() ? BackgroundStar : null,
        key: "star",
      },
    ],
  };

  return (
    <>
      {bgElements.startPage
        .filter((j) => j.element)
        .map((el, i) =>
          React.createElement(el.element, {
            key: el.key,
            isExiting: transitionState,
          }),
        )}
    </>
  );
};

export default StartPageBackground;
